import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoEssence({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Essence</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Auto Essence keeps your active essence slot filled. When the essence in it runs out, the
        bot puts in the first essence from your list that you have in the bag &mdash; the same as
        dragging it onto the slot yourself. Everything on this page is on the{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Essence</strong> tab.
      </p>

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Build your list">
        In the <strong>WHICH ESSENCE</strong> card, open <strong>Add</strong> and pick an essence.
        The dropdown lists the essences in your bag, with how many you have. Add up to six.
      </Step>
      <Step n={2} title="Put them in order">
        The first one you have goes in. Use the up and down buttons to put the one you want most
        on top; the cross removes a row.
      </Step>
      <Step n={3} title="Turn it on">
        Switch on <strong>Put in a new essence when it runs out</strong> in the{' '}
        <strong>AUTO ESSENCE</strong> card. A green dot shows next to <strong>Essence</strong> in
        the list on the left.
      </Step>

      <Shot
        name="auto-essence-01-tab.png"
        tab="AUTOPILOT › Essence"
        caption="Epic Essence of Vigor in the active slot, and a list of three: Epic first, then Rare, then Magic."
        shoot="menushot (stage essence): bekapcsolva, az aktív helyen Epic Essence of Vigor x37850, a listán Epic / Rare / Magic a színes ritkaság-pöttyökkel, mindháromból van a táskában."
      />

      <DocHeading>Telling the Essences Apart</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The game gives the five kinds of <em>Essence of Vigor</em> the same name &mdash; only the
        color differs. The list and the dropdown show a colored dot and the rarity after the name:
      </p>
      <DocTable
        headers={['Dot', 'Rarity']}
        rows={[
          [<span className="text-gray-400">&#9679;</span>, 'Common'],
          [<span className="text-emerald-400">&#9679;</span>, 'Uncommon'],
          [<span className="text-sky-400">&#9679;</span>, 'Magic'],
          [<span className="text-purple-400">&#9679;</span>, 'Rare'],
          [<span className="text-orange-400">&#9679;</span>, 'Epic'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Some essences also have an <strong className="text-white">event</strong> twin &mdash; for
        example the anniversary <em>Essence of Vigor</em>: same name, same rarity, same effect, but
        a separate item for the game. Those show <strong className="text-white">&middot; event</strong>{' '}
        after the rarity. The bot uses exactly the one on your list, so if you want both kinds used,
        add both.
      </p>

      <DocHeading>The Lines on the Card</DocHeading>
      <DocTable
        headers={['Line', 'What it shows']}
        rows={[
          [<strong className="text-white">Active slot</strong>, <>The essence in your active slot and how many are left, <span className="text-amber-300">empty</span>, or <span className="text-amber-300">empty - putting one in</span>.</>],
          [<strong className="text-white">From your list in the bag</strong>, <>How many essences from your list you still have. <span className="text-amber-300">none left</span> when they ran out.</>],
          [<strong className="text-white">Put in</strong>, 'How many times it filled the slot since the bot was loaded.'],
          [<strong className="text-white">Last try</strong>, 'Only shows when the game refused a try, with the reason.'],
        ]}
      />

      <DocHeading>Good to Know</DocHeading>
      <ul className="list-disc pl-5 text-sm text-gray-400 space-y-2 mb-6">
        <li>It only fills the <strong className="text-white">active</strong> essence slot. The second, swappable slot is left alone.</li>
        <li>It waits for the slot to be empty twice in a row before it acts, so a swap you make yourself is not interrupted.</li>
        <li>If the game refuses an essence (for example one for another class), that one is skipped for a minute and the next one on the list comes.</li>
        <li>When nothing from your list is left, it only tells you (at most every five minutes). It does not buy more.</li>
        <li>The list is shared between your clients, so it can hold essences of several classes &mdash; each client uses the first one it has and is allowed to use.</li>
        <li>It works in manual play too.</li>
      </ul>

      <Callout type="info">
        Health potions are handled on{' '}
        <button onClick={() => navigate('auto-potion')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Potion</button>.
      </Callout>
    </div>
  );
}

export default PageAutoEssence;
